import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { validateMemberPassword } from '../utils/memberPasswordPolicy';
import { notifyMemberSessionChanged, enrichMemberFromApi } from '../utils/memberSession';
import './ComunidadPage.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function syncPathToMode() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  if (path === '/comunidad/registro') return 'register';
  if (path === '/comunidad/recuperar') return 'forgot';
  return 'login';
}

const ComunidadPage = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(syncPathToMode);
  const [step, setStep] = useState(1);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPassword2, setRegPassword2] = useState('');

  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const regPwCheck = useMemo(() => validateMemberPassword(regPassword), [regPassword]);

  useEffect(() => {
    const onPop = () => {
      const m = syncPathToMode();
      setMode(m);
      if (m !== 'register') setStep(1);
      setError('');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigateSub = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    const m = syncPathToMode();
    setMode(m);
    setStep(1);
    setError('');
    setForgotSent(false);
  };

  const postJson = async (path, body) => {
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { ok, data } = await postJson('/auth/member-login', {
        email: email.trim(),
        password
      });
      if (!ok) {
        setError(data.message || t('comunidadPanel.loginError'));
        return;
      }
      const inner = data.data;
      if (inner?.token) {
        localStorage.setItem('javiland_member_token', inner.token);
        localStorage.setItem('javiland_member_user', JSON.stringify(enrichMemberFromApi(inner.member || {})));
      }
      notifyMemberSessionChanged();
      window.history.pushState({}, '', '/perfil');
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (err) {
      setError(t('comunidadPanel.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (regPassword !== regPassword2) {
      setError(t('comunidadPanel.passwordMismatch'));
      return;
    }
    if (!regPwCheck.ok) {
      setError(t('comunidadPanel.passwordPolicyError'));
      return;
    }
    setLoading(true);
    try {
      const { ok, data } = await postJson('/auth/member-register-start', {
        email: email.trim(),
        password: regPassword,
        first_name: firstName.trim(),
        last_name: lastName.trim()
      });
      if (!ok) {
        setError(data.message || t('comunidadPanel.registerError'));
        return;
      }
      setStep(2);
    } catch (err) {
      setError(t('comunidadPanel.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { ok, data } = await postJson('/auth/member-forgot-password', {
        email: forgotEmail.trim()
      });
      if (!ok) {
        setError(data.message || t('comunidadPanel.forgotError'));
        return;
      }
      setForgotSent(true);
    } catch (err) {
      setError(t('comunidadPanel.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { ok, data } = await postJson('/auth/member-verify-otp', {
        email: email.trim(),
        code: code.trim()
      });
      if (!ok) {
        setError(data.message || t('comunidadPanel.otpWrong'));
        return;
      }
      const inner = data.data;
      if (inner?.token) {
        localStorage.setItem('javiland_member_token', inner.token);
        localStorage.setItem('javiland_member_user', JSON.stringify(enrichMemberFromApi(inner.member || {})));
      }
      notifyMemberSessionChanged();
      window.history.pushState({}, '', '/perfil');
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (err) {
      setError(t('comunidadPanel.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setLoading(true);
    try {
      const { ok, data } = await postJson('/auth/member-resend-otp', { email: email.trim() });
      if (!ok) {
        setError(data.message || t('comunidadPanel.resendError'));
      }
    } catch (err) {
      setError(t('comunidadPanel.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  /* ——— Login ——— */
  if (mode === 'login') {
    return (
      <div className="comunidad-page">
        <div className="comunidad-card">
          <div className="comunidad-brand">
            <p className="comunidad-kicker">{t('comunidadPanel.kicker')}</p>
            <h1>{t('comunidadPanel.loginTitle')}</h1>
            <p className="comunidad-lead">{t('comunidadPanel.loginLead')}</p>
          </div>
          <form onSubmit={handleLogin} className="comunidad-form">
            {error && <div className="comunidad-error" role="alert">{error}</div>}
            <label htmlFor="l-email">{t('comunidadPanel.email')}</label>
            <input
              id="l-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder={t('comunidadPanel.emailPlaceholder')}
            />
            <label htmlFor="l-pass">{t('comunidadPanel.password')}</label>
            <input
              id="l-pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder={t('comunidadPanel.passwordPlaceholderLogin')}
            />
            <div className="comunidad-help-row">
              <button type="button" className="comunidad-forgot" onClick={() => navigateSub('/comunidad/recuperar')}>
                {t('comunidadPanel.forgotLink')}
              </button>
            </div>
            <button type="submit" className="comunidad-btn-primary" disabled={loading}>
              {loading ? t('comunidadPanel.loggingIn') : t('comunidadPanel.loginCta')}
            </button>
            <p className="comunidad-form-footer comunidad-form-footer-split">
              <span>{t('comunidadPanel.newUserPrompt')} </span>
              <button type="button" className="comunidad-inline-link" onClick={() => navigateSub('/comunidad/registro')}>
                {t('comunidadPanel.createAccount')}
              </button>
            </p>
          </form>
        </div>
      </div>
    );
  }

  /* ——— Registro paso OTP ——— */
  if (mode === 'register' && step === 2) {
    return (
      <div className="comunidad-page">
        <div className="comunidad-card">
          <div className="comunidad-brand">
            <p className="comunidad-kicker">{t('comunidadPanel.kicker')}</p>
            <h1>{t('comunidadPanel.otpTitle')}</h1>
            <p className="comunidad-lead">{t('comunidadPanel.otpLead', { email })}</p>
          </div>
          <form onSubmit={handleVerifyOtp} className="comunidad-form">
            {error && <div className="comunidad-error" role="alert">{error}</div>}
            <label htmlFor="com-code">{t('comunidadPanel.codeLabel')}</label>
            <input
              id="com-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={8}
              autoComplete="one-time-code"
              placeholder={t('comunidadPanel.codePlaceholder')}
              className="comunidad-code-input"
            />
            <button type="submit" className="comunidad-btn-primary" disabled={loading}>
              {loading ? t('comunidadPanel.verifying') : t('comunidadPanel.ctaStep2')}
            </button>
            <button type="button" className="comunidad-btn-link" onClick={handleResend} disabled={loading}>
              {t('comunidadPanel.resend')}
            </button>
            <p className="comunidad-form-footer">
              <button type="button" className="comunidad-inline-link" onClick={() => { setStep(1); setError(''); }}>
                {t('comunidadPanel.backToForm')}
              </button>
            </p>
          </form>
        </div>
      </div>
    );
  }

  /* ——— Registro paso 1 ——— */
  if (mode === 'register') {
    return (
      <div className="comunidad-page">
        <div className="comunidad-card">
          <div className="comunidad-brand">
            <p className="comunidad-kicker">{t('comunidadPanel.kicker')}</p>
            <h1>{t('comunidadPanel.registerTitle')}</h1>
            <p className="comunidad-lead">{t('comunidadPanel.registerLead')}</p>
          </div>
          <form onSubmit={handleRegister} className="comunidad-form">
            {error && <div className="comunidad-error" role="alert">{error}</div>}
            <label htmlFor="r-fn">{t('comunidadPanel.firstName')}</label>
            <input id="r-fn" value={firstName} onChange={(e) => setFirstName(e.target.value)} required autoComplete="given-name" />
            <label htmlFor="r-ln">{t('comunidadPanel.lastName')}</label>
            <input id="r-ln" value={lastName} onChange={(e) => setLastName(e.target.value)} required autoComplete="family-name" />
            <label htmlFor="r-email">{t('comunidadPanel.email')}</label>
            <input
              id="r-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder={t('comunidadPanel.emailPlaceholder')}
            />
            <label htmlFor="r-p1">{t('comunidadPanel.password')}</label>
            <input
              id="r-p1"
              type="password"
              value={regPassword}
              onChange={(e) => setRegPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
            <label htmlFor="r-p2">{t('comunidadPanel.confirmPassword')}</label>
            <input
              id="r-p2"
              type="password"
              value={regPassword2}
              onChange={(e) => setRegPassword2(e.target.value)}
              required
              autoComplete="new-password"
            />
            <ul className="member-pw-rules" aria-live="polite">
              <li className={regPwCheck.rules.length ? 'ok' : ''}>{t('comunidadPanel.ruleLength')}</li>
              <li className={regPwCheck.rules.lower ? 'ok' : ''}>{t('comunidadPanel.ruleLower')}</li>
              <li className={regPwCheck.rules.upper ? 'ok' : ''}>{t('comunidadPanel.ruleUpper')}</li>
              <li className={regPwCheck.rules.digit ? 'ok' : ''}>{t('comunidadPanel.ruleDigit')}</li>
              <li className={regPwCheck.rules.special ? 'ok' : ''}>{t('comunidadPanel.ruleSpecial')}</li>
            </ul>
            <button type="submit" className="comunidad-btn-primary" disabled={loading}>
              {loading ? t('comunidadPanel.sending') : t('comunidadPanel.registerCta')}
            </button>
            <p className="comunidad-form-footer comunidad-form-footer-split">
              <span>{t('comunidadPanel.haveAccountPrompt')} </span>
              <button type="button" className="comunidad-inline-link" onClick={() => navigateSub('/comunidad')}>
                {t('comunidadPanel.loginLink')}
              </button>
            </p>
          </form>
        </div>
      </div>
    );
  }

  /* ——— Recuperar ——— */
  if (mode === 'forgot') {
    return (
      <div className="comunidad-page">
        <div className="comunidad-card">
          <div className="comunidad-brand">
            <p className="comunidad-kicker">{t('comunidadPanel.kicker')}</p>
            <h1>{t('comunidadPanel.forgotTitle')}</h1>
            <p className="comunidad-lead">{t('comunidadPanel.forgotLead')}</p>
          </div>
          {forgotSent ? (
            <div className="comunidad-form">
              <p className="comunidad-hint">{t('comunidadPanel.forgotSent')}</p>
              <button type="button" className="comunidad-btn-primary" onClick={() => navigateSub('/comunidad')}>
                {t('comunidadPanel.backToLogin')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgot} className="comunidad-form">
              {error && <div className="comunidad-error" role="alert">{error}</div>}
              <label htmlFor="f-email">{t('comunidadPanel.email')}</label>
              <input
                id="f-email"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder={t('comunidadPanel.emailPlaceholder')}
              />
              <button type="submit" className="comunidad-btn-primary" disabled={loading}>
                {loading ? t('comunidadPanel.sending') : t('comunidadPanel.forgotCta')}
              </button>
              <p className="comunidad-form-footer">
                <button type="button" className="comunidad-inline-link" onClick={() => navigateSub('/comunidad')}>
                  {t('comunidadPanel.rememberPassword')}
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default ComunidadPage;
